import React, { useCallback, useState } from 'react'
import PropTypes from 'prop-types'
import { useQueryClient } from '@tanstack/react-query'
import { makeStyles } from '@material-ui/core'
import clsx from 'clsx'
import SydButton from '../../../../../commonDesign/Button'
import DebouncedInput from '../../../../../molecules/DebouncedInput'
import Loading from '../../../../../molecules/Loading'
import Card from '../../../../../molecules/Card'
import { useAssociateImage, useListImages } from '../../../../../../api/media'
import { QUERY_KEYS } from '../../../../../../api/queryKeys'

const useStyles = makeStyles((theme) => ({
  libraryGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))',
    gap: '12px',
    maxHeight: '300px',
    overflowY: 'auto',
    '&.__disabled': {
      pointerEvents: 'none',
      opacity: 0.6
    }
  },
  libraryItem: {
    width: '80px',
    height: '80px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    border: '2px solid transparent',
    borderRadius: 'var(--border-radius-md)',
    transition: 'border-color 0.2s',
    '&:hover': {
      borderColor: 'var(--color-primary-500)'
    },
    '& img': {
      maxWidth: '100%',
      maxHeight: '100%',
      objectFit: 'contain'
    }
  }
}))

function LibrarySelector ({
  onClose,
  purposeId,
  statusId
}) {
  const classes = useStyles()
  const queryClient = useQueryClient()
  const [searchTerm, setSearchTerm] = useState('')
  const { mutateAsync: associateImage, isLoading, error } = useAssociateImage()

  const { data: libraryImages, isLoading: libraryLoading } = useListImages({
    search: searchTerm,
    take: 20,
    sortBy: 'displayName',
    sortDir: 'asc'
  })

  const onSelectImage = useCallback(async (image) => {
    if (!purposeId) return
    await associateImage({
      imageId: image.imageId,
      purposeId: purposeId,
      levelTypeId: 279,
      levelId: statusId
    })

    onClose?.()
    await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.statusReportStatuses] })
  }, [associateImage, purposeId, statusId, queryClient, onClose])

  return (
    <div className='flex flex-col gap-md flex-1'>
      <div className='flex justify-between items-center gap-md'>
        <div className='font-bold font-size-sm'>Select from Library</div>
        <SydButton
          size='sm'
          variant='secondary'
          disabled={isLoading}
          processing={isLoading}
          onClick={() => {
            onClose?.()
            setSearchTerm('')
          }}
        >
          Back
        </SydButton>
      </div>
      <div>
        <DebouncedInput
          placeholder='Search images...'
          onChange={setSearchTerm}
          size='sm'
        />
      </div>
      {error && (
        <div className='text-danger-700 font-size-sm'>
          Error associating image: {error.message}
        </div>
      )}

      {libraryLoading ? (
        <Loading />
      ) : (
        <div className={clsx(classes.libraryGrid, { __disabled: isLoading })}>
          {(libraryImages?.data || []).map((image) => (
            <Card
              key={image.imageId}
              className={classes.libraryItem}
              onClick={() => onSelectImage(image)}
              title={image.displayName || image.description}
            >
              <img src={image.imageUrl} alt={image.description || 'Library image'} />
            </Card>
          ))}
          {!libraryImages?.data?.length && (
            <div style={{
              gridColumn: '1 / -1',
              textAlign: 'center',
              padding: '20px',
              color: 'var(--text-color-secondary)'
            }}
            >
              No images found
            </div>
          )}
        </div>
      )}
    </div>
  )
}

LibrarySelector.propTypes = {
  onClose: PropTypes.func,
  statusId: PropTypes.number,
  purposeId: PropTypes.number
}

export default LibrarySelector
